import * as React from "react";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import { Grid, Button } from "@material-ui/core";

// NPM
import clsx from "clsx";
import { animated, useTrail, config, interpolate } from "react-spring";

// Components
import { AnimatedDateHeader } from "./DateHeader.Component";
import { AnimatedAppointmentsAccordion } from "./AppointmentsAccordion.Component";
import { AnimatedAppointmentSlot } from "./AppointmentSlot.Component";
import ErrorDisplay from "../common/ErrorDisplay.Component";
import { AnimatedLoader } from "../common/Loader.Component";

// Interfaces
import IAppointmentRecord from "../../domain/interfaces/IAppointmentRecord";
import IAvailableSlot from "../../domain/interfaces/IAvailableSlot";

// Contexts
import { useCalendarManager } from "../../contexts/CalendarManager.Context";
import { useApplicationManager } from "../../contexts/ApplicationManager.Context";
import { useDoctorsManager } from "../../contexts/DoctorsManager.Context";

// Classes
import AppointmentRecord from "../../domain/classes/AppointmentRecord";
import Calendar from "../../domain/classes/Calendar";
import Utility from "../../domain/classes/Utility";
import Doctor from "../../domain/classes/Doctor";

// Errors
import UnableToRetrieveDoctorsError from "../../domain/classes/errors/UnableToRetrieveDoctorsError";

// Interfaces
import IRequestResponse, {
	InitialRquestResponseState,
} from "../../domain/interfaces/IRequestResponse";
import IDoctor from "../../domain/interfaces/IDoctor";
import AvailableSlot from "../../domain/classes/AvailableSlot";
import IAppointmentTypeProviderDto from "../../domain/interfaces/IAppointmentTypeProviderDto";

// S3 Assets
import { SAD_COLOURED } from "../../domain/utility/config";
import IAnimatable from "../../domain/interfaces/IAnimatable";
import IAppointmentType from "../../domain/interfaces/IAppointmentType";

import {v4 as uuidv4} from 'uuid';
import { AxiosError } from "axios";

export const styles = (theme: Theme) =>
	createStyles({
		root: { marginTop: theme.spacing(2) },
	});

const filterAvailableSlot = (
	apptTypeOverrides: IAppointmentTypeProviderDto[],
	rec: IAppointmentRecord,
	minDuration: number,
	selectedApptType: IAppointmentType,
	slot: IAvailableSlot,
	index: number,
	array: IAvailableSlot[]
) => {
	const overrideData = Doctor.getProviderOverrides(
		apptTypeOverrides,
		rec.userId
	);

	if (!overrideData) {
		minDuration = 0;
	}

	minDuration = !!!(overrideData as IAppointmentTypeProviderDto)
		.minimumDurationOverride
		? selectedApptType.minimumDuration
		: (overrideData as IAppointmentTypeProviderDto).minimumDurationOverride;
	return {
		__return: AvailableSlot.filterSlotDuration(
			slot,
			index,
			array,
			minDuration
		),
		minDuration,
	};
};

export interface AppointmentsContainerProps
	extends React.HTMLAttributes<HTMLDivElement>,
		IAnimatable {
	date: string;
	apptRecords: IAppointmentRecord[];
	apptRequestResponse: IRequestResponse;
}

const AppointmentsContainer = React.forwardRef<
	HTMLDivElement,
	AppointmentsContainerProps & WithStyles<typeof styles>
>(
	(
		{
			classes,
			className,
			date,
			apptRecords,
			apptRequestResponse,
			hasAnimation,
			...divProps
		},
		ref
	) => {
		// Contexts
		const { handleDateRangeChange, rangeSize } = useCalendarManager();

		const { apptTypeOverrides, patientDetails, selectedApptType } =
			useApplicationManager();
		const { handleDoctorsChange, doctors } = useDoctorsManager();

		// States
		const [firstAvailableError, setFirstAvailableError] =
			React.useState<string>("No appointment.");
		const [records, setRecords] = React.useState<IAppointmentRecord[]>([]);
		const [doctorsRequestResponse, setDoctorsRequestResponse] =
			React.useState<IRequestResponse>({ ...InitialRquestResponseState });

		React.useEffect(() => {
			if (doctorsRequestResponse.error) {
				return;
			}
			const result = apptRecords.filter((rec: IAppointmentRecord) =>
				AppointmentRecord.filterByAppointmentType(
					rec,
					apptTypeOverrides,
					selectedApptType,
					doctors,
					patientDetails
				)
			);

			setRecords(result);
		}, [
			apptRecords,
			apptTypeOverrides,
			patientDetails,
			selectedApptType,
			doctors,
			doctorsRequestResponse,
		]);

		// handler
		const handleFindFirstAvailable = async () => {
			try {
				const firstAvailableDate =
					await AppointmentRecord.findFirstAvailable();

				const dateRange = Calendar.generateDateRangeFrom(
					firstAvailableDate,
					rangeSize
				);

				handleDateRangeChange(dateRange);
			} catch (error) {
				setFirstAvailableError((error as AxiosError).message);
			}
		};

		// Callback
		const getDoctors = React.useCallback(async () => {
			setDoctorsRequestResponse((prevState: IRequestResponse) => ({
				...prevState,
				loading: true,
			}));
			try {
				const response = await Doctor.getAll();

				if (response.status !== 200) {
					throw new UnableToRetrieveDoctorsError();
				}

				response.data.map(
					(doctor: IDoctor) =>
						(doctor.TelehealthDays = JSON.parse(
							doctor.TelehealthDays as string
						))
				);

				handleDoctorsChange(response.data);
			} catch (error) {
				setDoctorsRequestResponse((prevState: IRequestResponse) => ({
					...prevState,
					error: "Unable to retrieve doctors. Please contact the clinic.",
				}));
			}
			setDoctorsRequestResponse((prevState: IRequestResponse) => ({
				...prevState,
				loading: false,
			}));
		}, [handleDoctorsChange]);

		React.useEffect(() => {
			getDoctors();
		}, [getDoctors]);

		// Animations
		const trail = useTrail(apptRecords.length, {
			config: config.wobbly,
			opacity: 1,
			x: 0,
			from: {
				opacity: 0,
				x: -20,
			},
		});

		let minDuration = 0;

		return (
			<div
				{...divProps}
				className={clsx(classes.root, className)}
				ref={ref}
			>
				<AnimatedDateHeader date={date} />

				{doctorsRequestResponse.error ? (
					<ErrorDisplay
						message={doctorsRequestResponse.error}
						src={SAD_COLOURED}
					/>
				) : (
					<React.Fragment>
						{apptRequestResponse.loading &&
						doctorsRequestResponse.loading ? (
							<AnimatedLoader />
						) : (
							<React.Fragment>
								{records.length === 0 &&
								!apptRequestResponse.loading ? (
									<Grid
										container
										spacing={2}
										direction="column"
									>
										<Grid item xs={12}>
											<ErrorDisplay
												message={firstAvailableError}
												src={SAD_COLOURED}
											/>
										</Grid>
										<Grid item xs={12}>
											<Button
												fullWidth
												color="primary"
												variant="contained"
												onClick={
													handleFindFirstAvailable
												}
											>
												First available
											</Button>
										</Grid>
									</Grid>
								) : (
									<Grid
										container
										spacing={1}
										direction="column"
									>
										{trail.map(
											(
												{ x, opacity, ...rest },
												index
											) => {
												const sortedRecords =
													records.sort(
														AvailableSlot.sortByTime
													);

												const rec =
													sortedRecords[index];

												if (!!!rec) {
													return <React.Fragment />;
												}

												return (
													<AnimatedAppointmentsAccordion
														key={uuidv4()}
														data={rec}
														style={{
															opacity,
															transform:
																interpolate(
																	[x],
																	(x) =>
																		`translateX(${x}px)`
																),
															...rest,
														}}
													>
														{rec.availableSlots.filter(
															(
																slot: IAvailableSlot,
																index: number,
																array: IAvailableSlot[]
															) => {
																let __return;
																({
																	__return,
																	minDuration,
																} = filterAvailableSlot(
																	apptTypeOverrides,
																	rec,
																	minDuration,
																	selectedApptType,
																	slot,
																	index,
																	array
																));
																return __return;
															}
														).map(
															(
																slot: IAvailableSlot
															) => {
																const dr =
																	Doctor.getDoctorByBPId(
																		slot.userId.toString(),
																		doctors
																	);

																return (
																	<AnimatedAppointmentSlot
																		key={JSON.stringify(
																			slot
																		)}
																		apptRec={
																			rec
																		}
																		availSlot={
																			slot
																		}
																		drData={
																			dr
																		}
																		minDuration={
																			minDuration
																		}
																	/>
																);
															}
														)}
													</AnimatedAppointmentsAccordion>
												);
											}
										)}
									</Grid>
								)}
							</React.Fragment>
						)}
					</React.Fragment>
				)}
			</div>
		);
	}
);

const StyledAppointmentsContainer = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("StyledAppointmentsContainer"),
})(AppointmentsContainer);

export default StyledAppointmentsContainer;

export const AnimatedAppointmentsContainer = animated(
	StyledAppointmentsContainer
);
